import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Создание или редактирование слота расписания внеурочки.
 *
 * При создании из ячейки сетки в `defaults` приходят day_of_week и timeId —
 * пользователь дополняет предметом, учителем, максимумом учеников и
 * датами действия.
 */
const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const EMPTY = {
  subjectId:    '',
  teacherId:    '',
  timeId:       '',
  dayOfWeek:    1,
  maxStudents:  '',  // пусто = без ограничения
  dateStart:    '',
  dateEnd:      '',
  archive:      false,
};

function shortTime(t) { return (t || '').slice(0, 5); }

export default function ExtracurricularSlotEditModal({
  mode, slotId, defaults, onClose, onSaved, onDeleted,
}) {
  const [data, setData]     = useState(EMPTY);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [times, setTimes]       = useState([]);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  // Справочники.
  useEffect(() => {
    api.get('/api/extracurricular-subjects').then((r) => setSubjects((r.rows || []).filter((s) => !s.archive))).catch(() => {});
    api.get('/api/extracurricular-times').then((r) => setTimes((r.rows || []).filter((t) => !t.archive))).catch(() => {});
    api.get('/api/extracurricular-teachers').then((r) => setTeachers(r.rows || [])).catch(() => {});
  }, []);

  // Если режим редактирования — подгружаем слот точечно.
  useEffect(() => {
    if (mode !== 'edit' || !slotId) return;
    setLoading(true); setError(null);
    api.get('/api/extracurricular-slots/' + slotId)
      .then((res) => {
        const s = res.slot;
        if (!s) { setError('Слот не найден'); return; }
        setData({
          subjectId:   String(s.subjectId),
          teacherId:   String(s.teacherId),
          timeId:      String(s.timeId),
          dayOfWeek:   s.dayOfWeek,
          maxStudents: s.maxStudents > 0 ? String(s.maxStudents) : '',
          dateStart:   s.dateStart || '',
          dateEnd:     s.dateEnd   || '',
          archive:     !!s.archive,
        });
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, [mode, slotId]);

  // Defaults из создания через клик ячейки сетки.
  useEffect(() => {
    if (mode !== 'create' || !defaults) return;
    setData((d) => ({
      ...d,
      dayOfWeek: defaults.dayOfWeek ?? d.dayOfWeek,
      timeId:    defaults.timeId    != null ? String(defaults.timeId) : d.timeId,
      dateStart: defaults.dateStart || d.dateStart,
    }));
  }, [mode, defaults]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = {
        subjectId:   parseInt(data.subjectId, 10) || 0,
        teacherId:   parseInt(data.teacherId, 10) || 0,
        timeId:      parseInt(data.timeId, 10) || 0,
        dayOfWeek:   parseInt(data.dayOfWeek, 10) || 0,
        maxStudents: data.maxStudents === '' ? 0 : (parseInt(data.maxStudents, 10) || 0),
        dateStart:   data.dateStart,
        dateEnd:     data.dateEnd,
        archive:     data.archive,
      };
      const res = mode === 'create'
        ? await api.post('/api/extracurricular-slots', payload)
        : await api.put(`/api/extracurricular-slots/${slotId}`, payload);
      onSaved?.(res.slot);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!slotId) return;
    if (!confirm('Удалить слот? Если по нему есть проведённые занятия с учениками — операция отменится, и слот можно будет только перевести в архив.')) return;
    setSaving(true); setError(null);
    try {
      await api.delete('/api/extracurricular-slots/' + slotId);
      onDeleted?.(slotId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{mode === 'create' ? 'Новый слот расписания' : 'Редактирование слота'}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 24, color: 'var(--fg-muted)' }}>Загрузка…</div>
        ) : (
          <form onSubmit={submit} className="sd-edit__form" noValidate>
            <div className="sd-edit__body">
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Предмет *</label>
                <select className="sd-edit__select" value={data.subjectId}
                        onChange={(e) => set('subjectId', e.target.value)} required>
                  <option value="">— выберите —</option>
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Учитель *</label>
                <select className="sd-edit__select" value={data.teacherId}
                        onChange={(e) => set('teacherId', e.target.value)} required>
                  <option value="">— выберите —</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">День недели *</label>
                <select className="sd-edit__select" value={data.dayOfWeek}
                        onChange={(e) => set('dayOfWeek', parseInt(e.target.value, 10))} required>
                  {DAY_LABELS.map((lbl, i) => (
                    <option key={i} value={i + 1}>{lbl}</option>
                  ))}
                </select>
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">Временной слот *</label>
                <select className="sd-edit__select" value={data.timeId}
                        onChange={(e) => set('timeId', e.target.value)} required>
                  <option value="">— выберите —</option>
                  {times.map((t) => (
                    <option key={t.id} value={t.id}>
                      {shortTime(t.timeStart)} – {shortTime(t.timeEnd)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Максимум учеников</label>
                <input
                  type="number" min="0" max="1000"
                  className="sd-edit__input"
                  value={data.maxStudents}
                  onChange={(e) => set('maxStudents', e.target.value)}
                  placeholder="0 или пусто — без ограничения"
                />
              </div>

              <div className="sd-edit__field">
                <label className="sd-edit__label">Действует с *</label>
                <input
                  type="date" className="sd-edit__input"
                  value={data.dateStart}
                  onChange={(e) => set('dateStart', e.target.value)}
                  required
                />
              </div>
              <div className="sd-edit__field">
                <label className="sd-edit__label">Действует по *</label>
                <input
                  type="date" className="sd-edit__input"
                  value={data.dateEnd}
                  onChange={(e) => set('dateEnd', e.target.value)}
                  required
                />
              </div>

              {mode === 'edit' && (
                <div className="sd-edit__field sd-edit__field--full">
                  <label className="ref-checkbox">
                    <input
                      type="checkbox"
                      checked={data.archive}
                      onChange={(e) => set('archive', e.target.checked)}
                    />
                    <span>В архиве (скрыт из расписания)</span>
                  </label>
                </div>
              )}
            </div>

            <div className="sd-edit__foot">
              {error && <div className="sd-edit__error">{error}</div>}
              {mode === 'edit' && (
                <button type="button" className="btn btn--sm btn--danger"
                        onClick={onDelete} disabled={saving}>
                  Удалить
                </button>
              )}
              <div className="sd-edit__foot-spacer" />
              <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
              <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
                {saving ? 'Сохраняем…' : (mode === 'create' ? 'Создать' : 'Сохранить')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
